// src/components/landing/LandingPage.jsx
import React, { useCallback, useEffect, useState } from 'react';
import '../../styles/landing.css';
import LandingHeader from './LandingHeader';
import Hero from './Hero';
import ProofStrip from './ProofStrip';
import ValueGrid from './ValueGrid';
import HowItWorks from './HowItWorks';
import WhyDifferent from './WhyDifferent';
import ForStakeholders from './ForStakeholders';
import AnalysisShowcase from './AnalysisShowcase';
import CTA from './CTA';
import SiteFooter from './SiteFooter';
import SignInDialog from './SignInDialog';
import { prefersReducedMotion } from '../../utils/reveal';

/**
 * The public page. Shown by AuthGate whenever there is no live session; the
 * dashboard is only reachable by signing in through the Login dialog here.
 */
export default function LandingPage() {
  const [signInOpen, setSignInOpen] = useState(false);
  const [presetRole, setPresetRole] = useState(null);

  // Arriving here after signing out should start at the top, not wherever the
  // page was last scrolled to.
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Scroll to a section, then move focus to its heading, so a keyboard or screen
  // reader user lands where the link said rather than staying at the top.
  const goTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    const heading = el.querySelector('h1, h2');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }, []);

  // Called with a role key from a stakeholder card, or with a click event from a
  // plain "Request access" button — only the string preselects a role.
  const requestAccess = useCallback((role) => {
    if (typeof role === 'string') setPresetRole(role);
    goTo('access');
  }, [goTo]);

  const openSignIn = useCallback(() => setSignInOpen(true), []);

  return (
    <div className="lp">
      <a className="lp-skip" href="#main">Skip to content</a>
      <LandingHeader onNavigate={goTo} onSignIn={openSignIn} onRequestAccess={requestAccess} />
      <main id="main" className="lp-main" tabIndex={-1}>
        <Hero onRequestAccess={requestAccess} onSeeHow={() => goTo('how')} />
        <ProofStrip />
        <ValueGrid />
        <HowItWorks />
        <WhyDifferent />
        <ForStakeholders onRequestAccess={requestAccess} />
        <AnalysisShowcase />
        {/* Team (founders) is off the page for now; Team.jsx is kept to bring it back. */}
        <CTA presetRole={presetRole} onSignIn={openSignIn} />
      </main>
      <SiteFooter onNavigate={goTo} onSignIn={openSignIn} />
      <SignInDialog open={signInOpen} onClose={() => setSignInOpen(false)} />
    </div>
  );
}
